// Mirrors the doctor console's Order Management -> New Order -> Investigations
// tab (search Lab/Radiology services, build a cart, Sign & Send to
// add_patient_lab_tests/) — same API calls, restyled UI, embedded here as one
// section of the OT Charge Capture form. Takes the shared patient selected at
// the top of the form (see PatientPickerSection) rather than picking its own.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box, Grid, Typography, IconButton, Chip, Checkbox, FormControlLabel, CircularProgress, Paper,
} from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import dayjs from 'dayjs';
import SectionCard from '../inventory/components/SectionCard';
import InvButton from '../inventory/components/InvButton';
import { InvTextField, InvSelect } from '../inventory/components/InvField';
import { searchChargeCaptureServices, investigationCategoryToGroupCode } from '../ipd-billing/billing-api';
import {
  PRIORITY_OPTIONS, SERVICE_TYPE_OPTIONS, DEPARTMENT_OPTIONS, INSTRUCTION_OPTIONS,
  mapMedicalServiceToInvestigationCard,
  fetchInvestigationSchema,
} from './labRadOrderApi';
import { IPD_COLORS, INV_RADIUS, INV_TYPE } from '../inventory/theme';
import { OT_SECTION_SX } from './otTheme';

const SEARCH_DEBOUNCE_MS = 300;
const LAB_RAD_TITLE = 'Laboratory & Radiology Charges';

const serviceTypeOptions = SERVICE_TYPE_OPTIONS.map(v => ({ value: v, label: v }));
const departmentOptions = DEPARTMENT_OPTIONS.map(v => ({ value: v, label: v }));
const priorityOptions = PRIORITY_OPTIONS.map(v => ({ value: v, label: v }));

function newPendingItem(item) {
  return {
    ...item,
    qty: 1,
    date: dayjs().format('YYYY-MM-DD'),
    priority: 'Routine',
    portable: false,
    doctorInstructions: '',
    testDescription: '',
    emergency: false,
    instruction: '',
  };
}

function ResultCard({ item, selected, onToggle }) {
  const subline = [item.category, item.serviceType, item.cpt && `CPT ${item.cpt}`].filter(Boolean).join(' · ');
  return (
    <Paper
      variant="outlined"
      onClick={() => onToggle(item)}
      sx={{
        p: 1.5, borderRadius: `${INV_RADIUS.card}px`, cursor: 'pointer',
        border: selected ? `2px solid ${IPD_COLORS.primary}` : `1px solid ${IPD_COLORS.border}`,
        backgroundColor: selected ? IPD_COLORS.selectedBg : IPD_COLORS.cardBg,
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': { borderColor: IPD_COLORS.primary },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: IPD_COLORS.textPrimary, lineHeight: 1.3 }}>
          {item.name}
        </Typography>
        <Box sx={{
          width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
          backgroundColor: selected ? IPD_COLORS.primary : 'transparent',
          border: `2px solid ${selected ? IPD_COLORS.primary : IPD_COLORS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {selected ? <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#fff' }} /> : <AddIcon sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }} />}
        </Box>
      </Box>
      {subline && (
        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 0.5 }}>{subline}</Typography>
      )}
      {item.interface && (
        <Chip label={item.interface} size="small" sx={{ mt: 0.75, height: 20, fontSize: 11, fontWeight: 600 }} />
      )}
    </Paper>
  );
}

function CartItemFields({ item, doctorName, instructionOptions, onUpdate }) {
  return (
    <Grid container spacing={1.5}>
      <Grid item xs={12}>
        <InvTextField
          label="Test description"
          placeholder="Test description (optional)"
          value={item.testDescription || ''}
          onChange={e => onUpdate('testDescription', e.target.value)}
        />
      </Grid>
      <Grid item xs={4}>
        <InvTextField label="CPT" value={item.cpt || '—'} disabled />
      </Grid>
      <Grid item xs={4}>
        <InvTextField
          label="Qty"
          type="number"
          value={item.qty}
          disabled={item.quantityModifiable === false}
          onChange={e => onUpdate('qty', Math.max(1, parseInt(e.target.value, 10) || 1))}
          inputProps={{ min: 1 }}
        />
      </Grid>
      <Grid item xs={4}>
        <InvTextField
          label="Rate"
          type="number"
          value={item.rate ?? ''}
          disabled={item.chargeModifiable !== true}
          onChange={e => onUpdate('rate', e.target.value === '' ? '' : Number(e.target.value))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InvTextField
          label="Service date"
          type="date"
          value={item.date}
          onChange={e => onUpdate('date', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InvSelect
          label="Priority *"
          value={item.priority}
          onChange={e => onUpdate('priority', e.target.value)}
          options={priorityOptions}
        />
      </Grid>
      <Grid item xs={12}>
        <InvSelect
          label="Instruction"
          value={item.instruction || ''}
          onChange={e => onUpdate('instruction', e.target.value)}
          options={instructionOptions.map(v => ({ value: v, label: v }))}
        />
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={Boolean(item.emergency)} onChange={e => onUpdate('emergency', e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>Emergency</Typography>}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={Boolean(item.portable)} onChange={e => onUpdate('portable', e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>Portable</Typography>}
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
          Order by: <Typography component="span" sx={{ fontWeight: 600, color: IPD_COLORS.textPrimary }}>{doctorName || '—'}</Typography>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <InvTextField
          label="Doctor instructions"
          multiline
          rows={2}
          value={item.doctorInstructions || ''}
          onChange={e => onUpdate('doctorInstructions', e.target.value)}
        />
      </Grid>
    </Grid>
  );
}

function CartPanel({ pendingItems, doctorName, instructionOptions, onRemove, onUpdate }) {
  const [expandedIds, setExpandedIds] = useState(() => new Set(pendingItems.length ? [pendingItems[pendingItems.length - 1].id] : []));
  const prevLenRef = useRef(pendingItems.length);

  useEffect(() => {
    const len = pendingItems.length;
    const idSet = new Set(pendingItems.map(p => p.id));
    if (len === 0) {
      setExpandedIds(new Set());
    } else if (len > prevLenRef.current) {
      setExpandedIds(new Set([pendingItems[len - 1].id]));
    } else {
      setExpandedIds(prev => {
        const next = new Set([...prev].filter(id => idSet.has(id)));
        if (next.size === 0) next.add(pendingItems[len - 1].id);
        return next;
      });
    }
    prevLenRef.current = len;
  }, [pendingItems]);

  const toggleExpanded = (id) => setExpandedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <Box sx={{
      border: `1px solid ${IPD_COLORS.border}`, borderRadius: `${INV_RADIUS.card}px`,
      backgroundColor: IPD_COLORS.cardBg, display: 'flex', flexDirection: 'column', height: '100%',
    }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${IPD_COLORS.border}` }}>
        <Typography sx={{ ...INV_TYPE.sectionTitle, color: IPD_COLORS.textPrimary }}>
          New investigation / service request · {pendingItems.length}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, minHeight: 220 }}>
        {pendingItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: IPD_COLORS.textSecondary }}>
            <ScienceOutlinedIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
            <Typography sx={{ fontSize: 13 }}>No investigations added yet.</Typography>
            <Typography sx={{ fontSize: 12, mt: 0.5, opacity: 0.7 }}>Add investigations from the list on the left.</Typography>
          </Box>
        ) : pendingItems.map((item, index) => {
          const expanded = expandedIds.has(item.id);
          return (
            <Box
              key={item.id}
              sx={{
                mb: 1.5, border: `1px solid ${expanded ? IPD_COLORS.primary : IPD_COLORS.border}`,
                borderRadius: `${INV_RADIUS.card}px`, overflow: 'hidden',
                boxShadow: expanded ? IPD_COLORS.shadow.card : 'none',
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                onClick={() => toggleExpanded(item.id)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpanded(item.id); } }}
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 0.5, p: 1.25, cursor: 'pointer',
                  backgroundColor: expanded ? IPD_COLORS.selectedBg : IPD_COLORS.cardBg,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Chip label={`${index + 1}/${pendingItems.length}`} size="small" sx={{ height: 18, fontSize: 11, fontWeight: 700 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: IPD_COLORS.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.name}
                    </Typography>
                  </Box>
                  {!expanded && (
                    <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textSecondary, mt: 0.3 }}>
                      {[item.category, item.priority, item.emergency && 'Emergency'].filter(Boolean).join(' · ')}
                      {item.qty ? ` · Qty ${item.qty}` : ''}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={e => { e.stopPropagation(); toggleExpanded(item.id); }} sx={{ color: IPD_COLORS.textSecondary }}>
                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small" onClick={e => { e.stopPropagation(); onRemove(item.id); }} sx={{ color: IPD_COLORS.error.text }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
              {expanded && (
                <Box sx={{ p: 1.5, pt: 1.75 }}>
                  <CartItemFields
                    item={item}
                    doctorName={doctorName}
                    instructionOptions={instructionOptions}
                    onUpdate={(field, value) => onUpdate(item.id, field, value)}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function LabRadChargesSection({ patient, pendingItems = [], onPendingItemsChange }) {
  const admissionId = patient?.admission_id;
  const doctorName = patient?.doctor_name;

  const [search, setSearch] = useState('');
  const [serviceType, setServiceType] = useState('All Service Types');
  const [department, setDepartment] = useState('Laboratory');
  const [searchResults, setSearchResults] = useState([]);
  const [searchStatus, setSearchStatus] = useState('idle'); // idle | loading | loaded | error
  const [searchError, setSearchError] = useState('');
  const searchSeqRef = useRef(0);
  const [instructionOptions, setInstructionOptions] = useState(INSTRUCTION_OPTIONS);

  useEffect(() => {
    if (!patient) return;
    // API: GET care_plan_structures/
    fetchInvestigationSchema()
      .then((schema) => {
        if (schema?.instructionOptions?.length) setInstructionOptions(schema.instructionOptions);
      })
      .catch(() => {});
  }, [patient]);

  const runSearch = useCallback((query, dept) => {
    const seq = ++searchSeqRef.current;
    if (!query.trim()) {
      setSearchResults([]);
      setSearchStatus('idle');
      setSearchError('');
      return;
    }
    if (!admissionId) {
      setSearchResults([]);
      setSearchStatus('error');
      setSearchError('Missing admission ID — cannot load patient-aware tariffs.');
      return;
    }
    setSearchStatus('loading');
    setSearchError('');
    const groupCode = investigationCategoryToGroupCode(dept);
    // API: GET ipd/billing/charge-capture/services/
    searchChargeCaptureServices(query, { limit: 50, admissionId, groupCode })
      .then((raw) => {
        if (seq !== searchSeqRef.current) return;
        const seenNames = new Set();
        const mapped = raw
          .map(mapMedicalServiceToInvestigationCard)
          .filter(Boolean)
          .sort((a, b) => {
            if (a._isHighQuality !== b._isHighQuality) return a._isHighQuality ? -1 : 1;
            return b._matchScore - a._matchScore;
          })
          .filter((item) => {
            const key = item.name.trim().toLowerCase();
            if (seenNames.has(key)) return false;
            seenNames.add(key);
            return true;
          });
        setSearchResults(mapped);
        setSearchStatus('loaded');
      })
      .catch((err) => {
        if (seq !== searchSeqRef.current) return;
        setSearchError(err.response?.data?.message || err.message || 'Could not load investigations.');
        setSearchStatus('error');
      });
  }, [admissionId]);

  useEffect(() => {
    const handle = window.setTimeout(() => runSearch(search, department), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [search, department, runSearch]);

  const displayResults = useMemo(() => {
    if (serviceType === 'All Service Types') return searchResults;
    return searchResults.filter(s => s.serviceType === serviceType);
  }, [searchResults, serviceType]);

  const isSelected = (item) => pendingItems.some(p => p.id === item.id);

  const toggleItem = (item) => {
    onPendingItemsChange((prev = []) => {
      if (prev.find(p => p.id === item.id)) return prev.filter(p => p.id !== item.id);
      return [...prev, newPendingItem(item)];
    });
  };

  const updateItem = (id, field, value) =>
    onPendingItemsChange((prev = []) => prev.map(p => (p.id === id ? { ...p, [field]: value } : p)));

  useEffect(() => {
    onPendingItemsChange([]);
  }, [patient]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {!patient && (
        <SectionCard title={LAB_RAD_TITLE} sx={OT_SECTION_SX}>
          <Box sx={{ px: 1.5, py: 1.25, border: `1px dashed ${IPD_COLORS.border}`, borderRadius: 1 }}>
            <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 14 }}>
              Select a patient above to order investigations.
            </Typography>
          </Box>
        </SectionCard>
      )}

      {patient && (
        <SectionCard title={LAB_RAD_TITLE} sx={OT_SECTION_SX}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 220px' }}>
                  <InvTextField
                    fullWidth
                    placeholder="Search labs, radiology, procedures, CPT code…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </Box>
                <Box sx={{ width: 170 }}>
                  <InvSelect fullWidth value={serviceType} onChange={e => setServiceType(e.target.value)} options={serviceTypeOptions} />
                </Box>
                <Box sx={{ width: 160 }}>
                  <InvSelect fullWidth value={department} onChange={e => setDepartment(e.target.value)} options={departmentOptions} />
                </Box>
              </Box>

              {searchStatus === 'loading' ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <CircularProgress size={28} sx={{ color: IPD_COLORS.primary, mb: 1.5 }} />
                  <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>Searching investigations…</Typography>
                </Box>
              ) : searchStatus === 'error' ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <ScienceOutlinedIcon sx={{ fontSize: 48, color: IPD_COLORS.border, mb: 1.5 }} />
                  <Typography sx={{ color: IPD_COLORS.error.text, fontSize: 13.5, mb: 1 }}>{searchError}</Typography>
                  <InvButton size="small" variant="outlined" onClick={() => runSearch(search, department)}>Retry</InvButton>
                </Box>
              ) : searchStatus === 'idle' ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <SearchIcon sx={{ fontSize: 48, color: IPD_COLORS.border, mb: 1.5 }} />
                  <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
                    Start typing to search labs, radiology, procedures, or CPT code.
                  </Typography>
                </Box>
              ) : displayResults.length > 0 ? (
                <Grid container spacing={1.25}>
                  {displayResults.map(item => (
                    <Grid item xs={12} sm={6} key={item.id}>
                      <ResultCard item={item} selected={isSelected(item)} onToggle={toggleItem} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <ScienceOutlinedIcon sx={{ fontSize: 48, color: IPD_COLORS.border, mb: 1.5 }} />
                  <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>No services match your filters.</Typography>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={5}>
              <CartPanel
                pendingItems={pendingItems}
                doctorName={doctorName}
                instructionOptions={instructionOptions}
                onRemove={id => onPendingItemsChange((prev = []) => prev.filter(p => p.id !== id))}
                onUpdate={updateItem}
              />
            </Grid>
          </Grid>
        </SectionCard>
      )}
    </>
  );
}
